import classNames from "classnames";
import { Entry } from "contentful";
import { ImgHTMLAttributes, useMemo } from "react";

import Editorials from "~/types/editorials";
import Constants from "~/utils/constants";
import imageUtils from "~/utils/image-utils";

import styles from "./media-asset.module.scss";
import OptimizedImage from "./optimized-image";
import VideoLazyLoad from "./video-lazy-load";

type Props = {
  entry: Entry<Editorials.ContentAwareAsset>;
  isWideSize?: boolean;
  className?: string;
  width?: number;
  height?: number;
  videoConfig?: {
    muted?: boolean;
    loop?: boolean;
    autoPlay?: boolean;
    controls?: boolean;
    playsInline?: boolean;
  };
  noLazy?: boolean;
} & Omit<ImgHTMLAttributes<HTMLImageElement>, "placeholder">;

const defaultVideoConfig = Constants.DEFAULT_VIDEO_CONFIG;

export default function OptimizedMediaAsset({
  entry,
  isWideSize,
  className,
  videoConfig = defaultVideoConfig,
  noLazy,
  ...rest
}: Props) {
  const editorialAsset = useMemo(
    () => (isWideSize ? entry.fields.desktopAsset : (entry.fields.mobileAsset as Entry<Editorials.Asset>)),
    [isWideSize, entry.fields]
  );
  const mediaUrl = useMemo(() => editorialAsset.fields.file.url, [editorialAsset]);
  const isVideo = useMemo(() => editorialAsset.fields.file.contentType.split("/")[0] !== "image", [editorialAsset]);
  const videoConfiguration = useMemo(() => {
    const { autoPlay, controls, loop, muted, playsInline } = entry.fields;
    return {
      autoPlay: autoPlay ?? videoConfig?.autoPlay ?? defaultVideoConfig.autoPlay,
      controls: controls ?? videoConfig?.controls ?? defaultVideoConfig.controls,
      loop: loop ?? videoConfig?.loop ?? defaultVideoConfig.loop,
      muted: muted ?? videoConfig?.muted ?? defaultVideoConfig.muted,
      playsInline: playsInline ?? videoConfig?.playsInline ?? defaultVideoConfig.playsInline,
    };
  }, [videoConfig, entry]);
  const videoPosterUrl = useMemo(() => {
    if (isVideo) {
      const { posterMobile, posterDesktop } = entry.fields;
      const poster = isWideSize ? posterDesktop : posterMobile;
      const posterUrl = poster && imageUtils.contentfulToCloudfrontSrcConverter(poster.fields?.file?.url);
      return posterUrl && `${posterUrl}?fm=webp`;
    }
  }, [entry, isWideSize, isVideo]);

  const videoMediaUrl = useMemo(() => {
    if (isVideo) {
      return imageUtils.contentfulToCloudfrontSrcConverter(mediaUrl);
    }
  }, [isVideo, mediaUrl]);

  return isVideo ? (
    <VideoLazyLoad
      {...videoConfiguration}
      className={classNames(styles.video, className)}
      poster={videoConfiguration.autoPlay ? undefined : videoPosterUrl}
      src={"https:" + videoMediaUrl + (videoPosterUrl || videoConfiguration.autoPlay ? "" : "#t=0.001")}
      preload={!!noLazy ? "auto" : "metadata"}
    />
  ) : (
    <OptimizedImage
      priority={!!noLazy}
      src={"https:" + mediaUrl}
      alt={editorialAsset.fields.description || editorialAsset.fields.title || ""}
      className={className}
      isWideSize={isWideSize}
      loader={imageUtils.contentfulImageLoader}
      {...rest}
    />
  );
}
